<template>
  <v-dialog
    v-model="open"
    persistent
    content-class="dialog-container"
    :max-width="dialogMaxWidth"
    :fullscreen="isMobileBreakpoint"
    @click:outside="handleCloseDialog"
    @input="handleCloseDialog"
  >
    <v-col v-if="step <= STEPS.UPLOAD" class="d-flex justify-space-between pb-0">
      <v-icon
        v-if="step > STEPS.INITIAL && step < STEPS.UPLOAD"
        @click="previousStep"
        class="close-icon ml-2 mr-2"
        size="24"
        color="grey"
        style="cursor: pointer"
      >
        mdi-arrow-left
      </v-icon>
      <v-toolbar-title
        v-if="step === STEPS.UPLOAD"
        class="body-regular-medium font-weight-medium ml-3"
        :class="{
          'neutral-20-dark--text': !$vuetify.theme.dark,
        }"
        style="line-height: 120%"
      >
        {{ $i18next.t('message.pages.video.replaceVideoModal.step_5.dialogTitle') }}
        <p
          v-if="!isMobileBreakpoint"
          class="text-caption mb-0"
          :class="{
            'neutral-40-light--text': !$vuetify.theme.dark,
          }"
        >
          {{ $i18next.t('message.pages.video.replaceVideoModal.step_5.dialogDescription') }}
          <a
            type="secondary"
            size="xsmall"
            @click="openLink"
          >
            {{ $i18next.t('message.pages.video.replaceVideoModal.step_5.learnMore') }}
            <v-icon size="12px" class="page__progressBar--article-icon">
              mdi-open-in-new
            </v-icon>
          </a>
        </p>
      </v-toolbar-title>
      <v-icon
        @click="handleCloseDialog"
        class="close-icon ml-auto"
        size="24"
        color="grey"
        style="cursor: pointer"
      >
        mdi-close
      </v-icon>
    </v-col>
    <v-card
      flat
      :style="{
        maxWidth: step === STEPS.UPLOAD ? '100%' : '822px',
        height: step === STEPS.UPLOAD ? 'auto' : '548px',
        margin: '0 auto'
      }"
      class="card-container d-flex flex-column"
      :class="{ 'justify-center': step === STEPS.INITIAL, 'justify-start': step !== STEPS.INITIAL }"
    >
      <v-card-text
        class="text-center"
        :class="`pa-${isMobileBreakpoint ? '5' : '7'}`"
        :style="isMobileBreakpoint ? { height: 'calc(100vh - 36px - 89px)', overflowY: 'auto' } : {}"
      >
        <div v-if="step === STEPS.INITIAL">
          <div no-gutters class="d-flex flex-column step-one">
            <div class="pa-0 mb-3 d-flex flex-column align-center">
              <img
                :src="require(`@/assets/replace-video.svg`)"
                class="mb-4"
                style="width: 64px; height: 64px"
              />
    
              <p class="title body-regular-xlarge text-center font-weight-medium neutral-20-dark--text mb-0">
                {{ $i18next.t('message.pages.video.replaceVideoModal.step_1.title') }}
              </p>
            </div>
            <div class="pa-0 mb-3 d-flex justify-center">
              <p class="body-regular-small text-center neutral-40-light--text mb-0">
                {{ getStepOneDescription() }}
              </p>
            </div>
            <div
              v-if="!isMobileBreakpoint"
              class="pa-0 mb-1 d-flex justify-center"
            >
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="isReplacing && !isMobileBreakpoint ? on : {}">
                    <base-btn
                      :disabled="isReplacing && !isMobileBreakpoint"
                      @click="nextStep"
                    >
                      {{ $i18next.t('message.pages.video.replaceVideoModal.actionBtnReplace') }}
                    </base-btn>
                  </span>
                </template>
                <span style="white-space: pre-line;">
                  {{ $i18next.t('message.pages.videoPage.details.alert.replacing.tooltipHeader') }}
                </span>
              </v-tooltip>
            </div>
            <div
              v-if="!isMobileBreakpoint"
              class="pa-0 d-flex justify-center"
            >
              <a @click="handleCloseDialog" type="secondary" size="xsmall">
                {{ $i18next.t('message.pages.video.replaceVideoModal.actionBtnReplaceLater') }}
              </a>
            </div>
          </div>
        </div>

        <div v-if="step === STEPS.CONFIRMATION">
          <div class="mt-0 d-flex flex-column align-start">
            <p class="title body-bold-medium neutral-20-dark--text mb-4">
              {{ $i18next.t('message.pages.video.replaceVideoModal.step_2.title') }}
            </p>
            <div class="alert-container mb-6">
              <img
                :src="require(`@/assets/alert-solid.svg`)"
                style="width: 24px; height: 24px"
              />
              <p class="body-regular-small neutral-20-dark--text mb-0">
                {{ $i18next.t('message.pages.video.replaceVideoModal.step_2.alertDescription') }}
              </p>
            </div>
            <p class="body-bold-small neutral-20-dark--text mb-0">
              {{ $i18next.t('message.pages.video.replaceVideoModal.step_2.warningMessage') }}
            </p>
            <p class="body-regular-small neutral-40-light--text mb-6">
              {{ $i18next.t('message.pages.video.replaceVideoModal.step_2.actionSuggestion') }}
            </p>
            <div class="mb-0">
              <v-checkbox
                v-model="confirmAction"
                :label="$i18next.t('message.pages.video.replaceVideoModal.step_2.confirmationLabel')">
              </v-checkbox>
            </div>
          </div>
        </div>

        <div v-if="step === STEPS.AI_RESOURCES">
          <div class="mt-0 d-flex flex-column align-start">
            <p class="title body-bold-medium neutral-20-dark--text mb-4">
              {{ $i18next.t('message.pages.video.replaceVideoModal.step_3.title') }}
            </p>
            <div class="resource-container neutral-99 mb-6">
              <p class="body-bold-small neutral-20-dark--text mb-0">
                {{ $i18next.t('message.pages.video.replaceVideoModal.step_3.resourceMessage') }}
              </p>
              <div class="d-flex flex-wrap" style="gap: 8px 4px;">
                <v-tag 
                  v-for="(tag, index) in getAiResourceTags" 
                  :key="index"
                  type="default"
                >
                  {{ tag }}
                </v-tag>
              </div>
            </div>
            <div class="d-flex flex-column" style="gap: 16px;">
              <v-row 
                v-for="(option, index) in aiOptions" 
                :key="index" 
                no-gutters 
                style="gap: 16px;" 
                :class="{ 'pa-2 px-4': isMobileBreakpoint }"
              >
                <v-col cols="auto" style="width: 24px;" class="d-flex align-center">
                  <v-radio-group v-model="selectedAiOption">
                    <v-radio :value="option.value"></v-radio>
                  </v-radio-group>
                </v-col>
                <v-col class="flex-grow-1 d-flex flex-column align-start justify-center">
                  <p class="body-regular-small neutral-20-dark--text mb-0">
                    {{ option.title }}
                  </p>
                  <p class="body-regular-smaller neutral-40-light--text mb-0">
                    {{ option.description }}
                  </p>
                </v-col>
              </v-row>
            </div>
          </div>
        </div>

        <div v-if="step === STEPS.ADDITIONAL_RESOURCES">
          <div class="mt-0 d-flex flex-column align-start">
            <p class="title body-bold-medium neutral-20-dark--text mb-4">
              {{ $i18next.t('message.pages.video.replaceVideoModal.step_4.title') }}
            </p>
            <div class="resource-container neutral-99 mb-6">
              <p class="body-bold-small neutral-20-dark--text mb-0">
                {{ $i18next.t('message.pages.video.replaceVideoModal.step_3.resourceMessage') }}
              </p>
              <div class="d-flex flex-wrap" style="gap: 8px 4px;">
                <v-tag 
                  v-for="(tag, index) in getAdditionalResourceTags" 
                  :key="index"
                  type="default"
                >
                  {{ tag }}
                </v-tag>
              </div>
            </div>
            <div class="d-flex flex-column" style="gap: 16px;">
              <v-row 
                v-for="(option, index) in additionalOptions" 
                :key="index" 
                no-gutters 
                style="gap: 16px;" 
                :class="{ 'pa-2 px-4': isMobileBreakpoint }"
              >
                <v-col cols="auto" style="width: 24px;" class="d-flex align-center">
                  <v-radio-group v-model="selectedAdditionalOption">
                    <v-radio :value="option.value"></v-radio>
                  </v-radio-group>
                </v-col>
                <v-col class="flex-grow-1 d-flex flex-column align-start justify-center">
                  <p class="body-regular-small neutral-20-dark--text mb-0">
                    {{ option.title }}
                  </p>
                  <p class="body-regular-smaller neutral-40-light--text mb-0">
                    {{ option.description }}
                  </p>
                </v-col>
              </v-row>
            </div>
          </div>
        </div>

        <div v-if="step === STEPS.UPLOAD">
          <NewImportModal
            :uppyConfig="uppy"
          />
        </div>
      </v-card-text>
      <div class="card-actions" v-if="!(step === STEPS.UPLOAD || (step === STEPS.INITIAL && !isMobileBreakpoint))">
        <div class="d-flex align-center" v-if="!isMobileBreakpoint">
          <a
            type="secondary"
            size="xsmall"
            class="brand-60-base--text"
            style="font-size: 14px;"
            @click="openLink"
          >
            {{ $i18next.t('message.pages.video.replaceVideoModal.learnMore') }}
            <v-icon
              size="14px"
              class="page__progressBar--article-icon brand-60-base--text"
            >
              mdi-open-in-new
            </v-icon>
          </a>
        </div>
        <div class="d-flex justify-end flex-grow-1">
          <v-tooltip bottom v-if="isMobileBreakpoint && step === STEPS.INITIAL">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="isReplacing && !isMobileBreakpoint ? on : {}" style="width: 100%; display: block;">
                <base-btn
                  :disabled="isReplacing && !isMobileBreakpoint"
                  style="width: 100%;"
                  @click="nextStep"
                >
                  {{ $i18next.t('message.pages.video.replaceVideoModal.actionBtnReplace') }}
                </base-btn>
              </span>
            </template>
            <span style="white-space: pre-line;">
              {{ $i18next.t('message.pages.videoPage.details.alert.replacing.tooltipHeader') }}
            </span>
          </v-tooltip>
          <base-btn
            v-if="step !== STEPS.INITIAL"
            type="tertiary"
            :style="{ width: isMobileBreakpoint ? '50%' : '102px' }"
            @click="cancelReplace"
          >
            {{ $i18next.t('message.pages.video.replaceVideoModal.actionBtnCancel') }}
          </base-btn>
          <base-btn
            v-if="step !== STEPS.INITIAL"
            class="ml-3"
            :style="{ width: isMobileBreakpoint ? '50%' : '102px' }"
            :disabled="isConfirmBtnDisabled"
            @click="nextStep"
          >
            {{ $i18next.t('message.pages.video.replaceVideoModal.actionBtnConfirm') }}
          </base-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import videosApi from '@/services/videosApi'
import NewImportModal from '@/components/NewImportModal.vue'
import Storage from '@/utils/Storage'
import { mapMutations, mapState } from 'vuex'

// External imports
import i18n from '@/translations/lang'
import Uppy from '@uppy/core'
import Dashboard from '@uppy/dashboard'
import GoogleDrive from '@uppy/google-drive'
import tus from '@uppy/tus'
import { v4 as uuidv4 } from 'uuid'

import English from '@uppy/locales/lib/en_US'
import Spanish from '@uppy/locales/lib/es_ES'
import Portuguese from '@uppy/locales/lib/pt_BR'
import Onedrive from '@uppy/onedrive'
import Url from '@uppy/url'

import { UPLOADER_COMPANION_URL, BACKUP_UPLOADER_URL, ENV_CONFIG } from '../config/envs'

const STEPS = {
  INITIAL: 1,
  CONFIRMATION: 2,
  AI_RESOURCES: 3,
  ADDITIONAL_RESOURCES: 4,
  UPLOAD: 5
}

export default {
  name: 'ReplaceVideoModal',
  components: {
    NewImportModal
  },
  data() {
    return {
      STEPS,
      video: null,
      uploadFiles: [],
      uploadPaused: false,
      uploadProgress: 0,
      uploadOpened: false,
      uppyUpdated: 0,
      uppyLocale: Portuguese,
      open: false,
      isOpening: false,
      dialogMaxWidth: '825px',
      step: STEPS.INITIAL,
      confirmAction: false,
      selectedAiOption: '',
      selectedAdditionalOption: '',
      isReplacing: false,
      isCrispButtonVisible: true
    }
  },
  computed: {
    ...mapState('user', {
      isMobileBreakpoint: 'isMobileBreakpoint',
    }),
    currentLang() {
      return this.$i18next.resolvedLanguage || 'pt'
    },
    uppy() {
      return this.uppyConfig()
    },
    isConfirmBtnDisabled() {
      const conditions = {
        [this.STEPS.CONFIRMATION]: !this.confirmAction,
        [this.STEPS.AI_RESOURCES]: !this.selectedAiOption,
        [this.STEPS.ADDITIONAL_RESOURCES]: !this.selectedAdditionalOption
      }

      return conditions[this.step] ?? false
    },
    aiOptions() {
      return [
        {
          value: 'regenerate',
          title: this.$i18next.t('message.pages.video.replaceVideoModal.step_3.options.regenerate.title'),
          description: this.$i18next.t('message.pages.video.replaceVideoModal.step_3.options.regenerate.description')
        },
        {
          value: 'retain',
          title: this.$i18next.t('message.pages.video.replaceVideoModal.step_3.options.retain.title'),
          description: this.$i18next.t('message.pages.video.replaceVideoModal.step_3.options.retain.description')
        },
        {
          value: 'discard',
          title: this.$i18next.t('message.pages.video.replaceVideoModal.step_3.options.discard.title'),
          description: this.$i18next.t('message.pages.video.replaceVideoModal.step_3.options.discard.description')
        }
      ]
    },
    additionalOptions() {
      return [
        {
          value: 'retain',
          title: this.$i18next.t('message.pages.video.replaceVideoModal.step_4.options.retain.title'),
          description: this.$i18next.t('message.pages.video.replaceVideoModal.step_4.options.retain.description')
        },
        {
          value: 'discard',
          title: this.$i18next.t('message.pages.video.replaceVideoModal.step_4.options.discard.title'),
          description: this.$i18next.t('message.pages.video.replaceVideoModal.step_4.options.discard.description')
        }
      ]
    },
    getAiResourceTags() {
      if (!this.video?.config) return []
      
      const tags = []
      const { audios, config } = this.video

      if (config.subtitles?.length > 0) {
        tags.push(this.$i18next.t('message.pages.video.replaceVideoModal.tags.subtitle'))
      }

      if (config.audios?.length > 0 || audios?.length > 0) {
        tags.push(this.$i18next.t('message.pages.video.replaceVideoModal.tags.dubbing'))
      }

      if (config.ai) {
        tags.push(this.$i18next.t('message.pages.video.replaceVideoModal.tags.aiPackage'))
      }
      
      return tags
    },
    getAdditionalResourceTags() {
      if (!this.video?.config) return []
      
      const tags = []
      const { config } = this.video
      
      if (config.ctas?.length > 0 || config?.outsideCta?.length > 0) {
        const hasButtons = config.ctas?.some(item => item.metadata?.button || config.ctas?.some(item => item.type === 'video')) || config?.outsideCta?.length > 0
        const hasMiniTriggers = config.ctas?.some(item => item.metadata?.isMiniTrigger)
        const hasPauseThumbnail = config.ctas?.some(item => item.type === 'pause')

        if (hasButtons) {
          tags.push(this.$i18next.t('message.pages.video.replaceVideoModal.tags.button'))
        }
        
        if (hasMiniTriggers) {
          tags.push(this.$i18next.t('message.pages.video.replaceVideoModal.tags.miniTrigger'))
        }

        if (hasPauseThumbnail) {
          tags.push(this.$i18next.t('message.pages.video.replaceVideoModal.tags.pauseThumbnail'))
        }
      }
      
      if (config.bookMarks && Object.keys(config.bookMarks).length > 0) {
        tags.push(this.$i18next.t('message.pages.video.replaceVideoModal.tags.chapters'))
      }
      
      return tags
    }
  },
  mounted() {
    this.setupListeners()
  },
  destroyed() {
    this.removeListeners()
  },
  watch: {
    $route(to) {
      if (to.name === 'VideoPage') {
        this.setupListeners()
        this.resetModalState()
      } else {
        this.showCrispButton()
        this.removeListeners()
        this.open = false
      }
    },
    open(newVal) {
      if (newVal) {
        this.hideCrispButton()
      } else {
        this.showCrispButton()
      }
    },
    isMobileBreakpoint(newVal) {
      if (newVal && this.open) {
        this.hideCrispButton()
      } else {
        this.showCrispButton()
      }
    },
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    async handleOpenDialog() {
      if (this.isOpening || this.open) return

      const emitEvent = false
      this.isOpening = true
      this.dialogMaxWidth = '825px'
      this.step = this.STEPS.INITIAL

      if (this.isReplacing) {
        const isReplacingSuccess = await this.checkReplacingStatus()
        if (!isReplacingSuccess) {
          this.isOpening = false
          return
        }
      }

      this.checkReplacingStatus(emitEvent)

      this.open = true
      this.isOpening = false
    },
    handleCloseDialog() {
      this.open = false
      this.$root.$emit('closeReplaceVideoModal')
      this.setUppyEventsOff()
    },
    getStepOneDescription() {
      const description = this.$i18next.t('message.pages.video.replaceVideoModal.step_1.description')
      return this.isMobileBreakpoint ? description.replace('\n ', '') : description
    },
    hasAiResources() {
      if (!this.video?.config) return false
      const { audios, config } = this.video

      return (
        config.subtitles?.length > 0 || config.audios?.length > 0 || audios?.length > 0 || !!config.ai
      )
    },
    hasAdditionalResources() {
      if (!this.video?.config) return false
      const { config } = this.video

      const hasCtas = config.ctas?.length > 0 || config?.outsideCta?.length > 0 && (
        config.ctas?.some(item => item.metadata?.button) ||
        config.ctas?.some(item => item.metadata?.isMiniTrigger) ||
        config.ctas?.some(item => item.type === 'pause' || item.type === 'video') ||
        config?.outsideCta?.length > 0
      )
      
      const hasBookmarks = config.bookMarks && Object.keys(config.bookMarks).length > 0
      
      return hasCtas || hasBookmarks
    },
    changeStep(newStep) {
      const previousWidth = this.dialogMaxWidth

      if (newStep === 1) {
        this.dialogMaxWidth = '825px'
      } else if (newStep === 5) {
        this.dialogMaxWidth = '880px'
      } else {
        this.dialogMaxWidth = '640px'
      }

      if (previousWidth !== this.dialogMaxWidth) {
        this.open = false
        setTimeout(() => {
          this.step = newStep
          this.open = true
        }, 200)
      } else {
        this.step = newStep
      }
    },
    nextStep() {
      let nextStep = this.step + 1

      if (nextStep === this.STEPS.AI_RESOURCES && !this.hasAiResources()) {
        nextStep = this.STEPS.ADDITIONAL_RESOURCES
      }

      if (nextStep === this.STEPS.ADDITIONAL_RESOURCES && !this.hasAdditionalResources()) {
        nextStep = this.STEPS.UPLOAD
      }

      if (this.isReplacing && this.isMobileBreakpoint) {
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          type: 'warning',
          text: i18n.t('message.pages.videoPage.details.alert.replacing.tooltipHeader'),
        })
        return
      }

      this.changeStep(nextStep)
    },
    previousStep() {
      let previousStep = this.step - 1

      if (previousStep === this.STEPS.ADDITIONAL_RESOURCES && !this.hasAdditionalResources()) {
        previousStep = this.STEPS.AI_RESOURCES
      }

      if (previousStep === this.STEPS.AI_RESOURCES && !this.hasAiResources()) {
        previousStep = this.STEPS.CONFIRMATION
      }

      this.changeStep(previousStep)
    },
    getUserSelectionType() {
      const ai = this.selectedAiOption
      const additional = this.selectedAdditionalOption

      if (!ai && !additional) {
        return null
      }

      if (ai === 'regenerate' && additional === 'retain') return 'regenerate_ai_keep_options'
      if (ai === 'regenerate' && additional === 'discard') return 'regenerate_ai_remove_options'
      if (ai === 'retain' && additional === 'retain') return 'keep_options'
      if (ai === 'retain' && additional === 'discard') return 'remove_config_options'
      if (ai === 'discard' && additional === 'retain') return 'remove_ai_options'
      if (ai === 'discard' && additional === 'discard') return 'remove_options'

      if (ai === 'regenerate' && !additional) return 'regenerate_ai_keep_options'
      if (ai === 'retain' && !additional) return 'keep_options'
      if (ai === 'discard' && !additional) return 'remove_ai_options'
      if (!ai && additional === 'retain') return 'keep_options'
      if (!ai && additional === 'discard') return 'remove_config_options'

      return null
    },
    openLink() {
      const locale = this.$i18next.resolvedLanguage || 'pt'

      const HELP_LINKS = {
        pt: {
          [this.STEPS.CONFIRMATION]: 'https://help.pandavideo.com/pt-br/article/substituicao-de-video-15zji4k/',
          [this.STEPS.AI_RESOURCES]: 'https://help.pandavideo.com/pt-br/article/substituicao-de-video-15zji4k/#2-recursos-adicionais-gerar-novamente-manter-ou-remover',
          [this.STEPS.ADDITIONAL_RESOURCES]: 'https://help.pandavideo.com/pt-br/article/substituicao-de-video-15zji4k/#2-recursos-adicionais-gerar-novamente-manter-ou-remover',
          [this.STEPS.UPLOAD]: 'https://help.pandavideo.com/pt-br/article/substituicao-de-video-15zji4k/'
        },
        en: {
          [this.STEPS.CONFIRMATION]: 'https://help.pandavideo.com/en-us/article/video-replacement-m7vhzn/x',
          [this.STEPS.AI_RESOURCES]: 'https://help.pandavideo.com/en/article/video-replacement-15zji4k/#2-additional-resources-regenerate-keep-or-remove',
          [this.STEPS.ADDITIONAL_RESOURCES]: 'https://help.pandavideo.com/en/article/video-replacement-15zji4k/#2-additional-resources-regenerate-keep-or-remove',
          [this.STEPS.UPLOAD]: 'https://help.pandavideo.com/en/article/video-replacement-15zji4k/'
        },
        es: {
          [this.STEPS.CONFIRMATION]: 'https://help.pandavideo.com/es/article/reemplazo-de-video-1v5pwa4/',
          [this.STEPS.AI_RESOURCES]: 'https://help.pandavideo.com/es/article/sustitucion-de-video-15zji4k/#2-recursos-adicionales-generar-nuevamente-mantener-o-eliminar',
          [this.STEPS.ADDITIONAL_RESOURCES]: 'https://help.pandavideo.com/es/article/sustitucion-de-video-15zji4k/#2-recursos-adicionales-generar-nuevamente-mantener-o-eliminar',
          [this.STEPS.UPLOAD]: 'https://help.pandavideo.com/es/article/sustitucion-de-video-15zji4k/'
        }
      }

      const link = HELP_LINKS[locale]?.[this.step] || HELP_LINKS['pt'][this.step]

      if (link) {
        window.open(link, '_blank')
      }
    },
    showSuccessMessage() {
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        type: 'success',
        text: i18n.t('message.pages.video.replaceVideoModal.step_5.snackbar'),
      })
    },
    setupListeners() {
      this.removeListeners()

      this.$root.$on('openReplaceVideoModal', (video) => {
        this.video = video
        this.setUppyLanguage()
        this.handleOpenDialog()
      })
    },
    removeListeners() {
      this.$root.$off('openReplaceVideoModal')
      this.$root.$off('changeLanguage')
    },
    hideCrispButton() {
      const crispButton = document.querySelector('.crisp-client')
      const crispIcon = crispButton?.querySelector('.cc-1m2mf')
      if (crispIcon && this.isMobileBreakpoint && this.isCrispButtonVisible) {
        crispIcon.style.setProperty('display', 'none', 'important')
        this.isCrispButtonVisible = false
      }
    },
    showCrispButton() {
      const crispButton = document.querySelector('.crisp-client')
      const crispIcon = crispButton?.querySelector('.cc-1m2mf')
      if (crispIcon && !this.isCrispButtonVisible) {
        crispIcon.style.removeProperty('display')
        this.isCrispButtonVisible = true
      }
    },
    cancelReplace() {
      this.handleCloseDialog()
      this.resetModalState()
    },
    resetModalState() {
      this.step = this.STEPS.INITIAL
      this.confirmAction = false
      this.selectedAiOption = ''
      this.selectedAdditionalOption = ''
      this.isReplacing = false
    },
    async getVideo() {
      const response = await videosApi.getAVideo(this.video.id)
      return response.data
    },
    async checkReplacingStatus(emitEvent = true) {
      if (emitEvent) {
        this.$root.$emit('replaceVideoStarted', true)
      }
      let attempts = 0
      const maxAttempts = 10
      const interval = 2000

      const checkStatus = async () => {
        try {
          const data = await this.getVideo()
          
          if (data.is_replacing || data.status !== 'CONVERTED') {
            this.isReplacing = true
            this.$root.$emit('refreshVideoInfo')
            return true
          }

          attempts++
          
          if (attempts >= maxAttempts) {
            return false
          }

          await new Promise(resolve => setTimeout(resolve, interval))
          return checkStatus()
        } catch (error) {
          console.error('Error checking replacing status:', error)
          this.$root.$emit('replaceVideoStarted', false)
          return false
        }
      }

      return await checkStatus()
    },
    uppyConfig() {
      this.uppyUpdated
      const objTus = this.handleTusObject()
      if (!this.isChrome() || this.isMobile()) {
        objTus.resume = false
        objTus.parallelUploads = 1
      }
      let objCompanion = this.handleCompanionObj()
      const instanceUppy = new Uppy({
        
        autoProceed: true,
        allowMultipleUploads: true,
        restrictions: {
          allowedFileTypes: ['video/*', '.ts'],
          maxNumberOfFiles: 1
        },
        locale: this.uppyLocale,
      })
        .use(tus, objTus)
        .use(Dashboard, {
          doneButtonHandler: () => {
            this.handleCloseDialog()
          }
        })
        .use(Url, { ...objCompanion, id: 'url' })
        .use(GoogleDrive, { ...objCompanion, id: 'google-drive' })
        .use(Onedrive, { ...objCompanion, id: 'onedrive' })
      return instanceUppy
    },
    handleTusObject() {
      let uploaderUrl = `${BACKUP_UPLOADER_URL}/files/`
      if (ENV_CONFIG !== 'production') {
        uploaderUrl = `${BACKUP_UPLOADER_URL}:8443/files/`
      }
      const storageUrl = Storage.getItem('uploaderUrlv3')
      if (storageUrl) {
        uploaderUrl = `${storageUrl}/files/`
        if (ENV_CONFIG !== 'production') {
          uploaderUrl = `${storageUrl}:8443/files/`
        }
      }
      let objTus = {
        id: 'tus',
        endpoint: uploaderUrl,
        resume: true,
        retryDelays: [0, 1000],
        parallelUploads: 1,
        limit: 1,
      }
      if (!this.isChrome() || this.isMobile()) {
        objTus.resume = false
        objTus.parallelUploads = 1
      }
      return objTus
    },
    handleCompanionObj() {
      let companionUrl = `${UPLOADER_COMPANION_URL}:3020`
      if (ENV_CONFIG !== 'production') {
        companionUrl = `${UPLOADER_COMPANION_URL}:3021`
      }
      let objCompanion = {
        id: 'companion',
        target: Dashboard,
        companionUrl: companionUrl,
      }
      return objCompanion
    },
    getUppyLocale() {
      const LOCALES = {
        en: English,
        es: Spanish,
        pt: Portuguese
      }
      return LOCALES[this.$i18next.resolvedLanguage] || Portuguese
    },
    setUppyLanguage() {
      const uppyLocale = this.getUppyLocale()

      this.uppyLocale = {
        ...uppyLocale,
        strings: {
          ...uppyLocale.strings,
          uploadingXFiles: {
            0: this.$i18next.t('message.pages.video.replaceVideoModal.step_5.sendingFile')
          },
          uploadComplete: this.$i18next.t('message.pages.video.replaceVideoModal.step_5.sentFile')
        }
      }

      this.setUppyEventsOff()
      this.uppyUpdated++
      setTimeout(() => {
        this.setUppyEvents()
      }, 1000)
    },
    setUppyEventsOff() {
      this.uppy.off('file-added')
      this.uppy.off('upload-success')
      this.uppy.off('progress')
      this.uppy.off('restriction-failed')
      this.uppy.off('back-online')
      this.uppy.off('upload-error')
      this.uppy.off('complete')
    },
    setUppyEvents() {
      // eslint-disable-next-line no-unused-vars
      this.uppy.on('upload-error', async (file, error, response) => {
        try {
          let err = error.toString()
          if (err.match('reached the maximum allowed')) {
            this.$root.$emit('onStorageSizeMax')
            this.$root.$emit('importUploadError')
            this.$root.$emit('forcePauseUploadList')
            if (!this.isFetchingPlanInfo) {
              const _this = this
              this.$root.$emit('fixUserLimit', true)
              this.isFetchingPlanInfo = true
              this.$root.$on('loadedUserLimitToUppy', () => {
                const type = err.includes('storage') ? 'storage' : 'traffic'
                _this.isFetchingPlanInfo = false
  
                if (_this.planInfo.subscription.status !== 'trialing') {
                  _this.$root.$emit('usageLimitModal', {
                    open: true,
                    type,
                    storagePlan: _this.planInfo?.limit?.storage_plan,
                  })
                } else if (_this.planInfo.subscription.status === 'trialing') {
                  _this.$root.$emit('ModalResetBillingCycle')
                }
              })
            }
          } else if (
            err.match('no space left') ||
            err.match('endpoint might be blocked')
          ) {
            this.swapUploadServer(file)
          }
        } catch (e) {
          this.UPDATE_GLOBAL_SNACKBAR({ open: true, text: `${e}` })
        }
      })
      this.uppy.on('file-added', (file) => {
        let storageKeys = Storage.getStorageKeys()
        for (let key of storageKeys) {
          if (key[0].includes('tus::tus-uppy')) {
            Storage.removeItem(key[0])
          }
        }
        //Set dos metadados em vídeos importados de outra  plataforma
        let id = ''
        if (file.meta.folder_id) {
          id = file.meta.folder_id
        } else {
          id = this.$route.params.id
        }
        const userId = this.$store.getters['session/userSub']
        const refreshToken = this.$store.state.user.refreshToken
        const authorization = 'Bearer ' + Storage.getItem('accessToken')
        const uploadType = file.isRemote ? 'companion' : 'direct'
        const replace_video_options = this.getUserSelectionType()
        this.uppy.setFileMeta(file.id, {
          video_id: uuidv4(),
          folder_id: id,
          user_id: userId,
          refresh_token: refreshToken,
          authorization: authorization,
          upload_type: uploadType,
          should_replace: true,
          replace_video_options,
          replace_video_external_id: this.video.video_external_id,
          replace_video_id: this.video.id
        })
        this.uploadFiles = this.uppy.getFiles()
        this.uploadOpened = true
        if (this.uploadPaused) {
          this.uppy.resumeAll()
        }
      })
      this.uppy.on('upload-success', (file) => {
        this.showSuccessMessage()
        this.$root.$emit('fixUserLimit')
        const videoFormat = {
          title: file.name,
          id: file.meta.video_id,
          status: 'DRAFT',
          folder_id: file.meta.folder_id,
          updated_at: new Date().toISOString(),
        }
        this.uploadFiles = this.uppy.getFiles()
        this.checkReplacingStatus()
        let storagedVideosArray = Storage.getItem(
          `panda-queue-${this.$store.state.user.userInfo.sub}`
        )
        if (!storagedVideosArray) {
          Storage.setItem(
            `panda-queue-${this.$store.state.user.userInfo.sub}`,
            [videoFormat]
          )
        } else {
          storagedVideosArray.push(videoFormat)
          Storage.setItem(
            `panda-queue-${this.$store.state.user.userInfo.sub}`,
            storagedVideosArray
          )
        }
      })
      this.uppy.on('progress', (progress) => {
        this.uploadProgress = progress
        this.uploadFiles = this.uppy.getFiles()
      })
      this.uppy.on('restriction-failed', (file) => {
        if (file.extension === 'pdf') {
          this.UPDATE_GLOBAL_SNACKBAR({
            open: true,
            text: i18n.t('message.fileExtensionError'),
          })
        }
      })
      this.uppy.on('back-online', () => {
        this.uppy.retryAll()
      })
      this.uppy.on('complete', () => {
        this.observeDoneButton()
      })
    },
    observeDoneButton() {
      const doneButton = document.querySelector('.uppy-StatusBar-actionBtn--done')
      if (doneButton) {
        doneButton.addEventListener('click', () => this.handleCloseDialog())
      }
    },
    isChrome() {
      return !!window.chrome
    },
    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    },
  },
}
</script>

<style scoped lang="scss">
p {
  white-space: pre-line;
  text-align: left;
}

.toolbar-title {
  white-space: normal;
  word-break: break-word;
}

.v-dialog--fullscreen > .v-card {
  min-height: 100% !important;
  height: auto !important;
  min-width: 100%;
  margin: 0 !important;
}

.message-box {
  display: block;
  color: #636578 !important;
  z-index: 999;
  position: relative;
}

.base-input.v-input[data-v-36a1b07e] input::placeholder {
  font-weight: 400;
  font-size: 12px;
}

::v-deep .v-label {
  font-size: 14px !important;
  color: varFigma(neutral-40-light) !important;
}

::v-deep .v-dialog .close-icon {
  width: 24px;
  height: 24px;
  z-index: 1;
}

::v-deep .dialog-container {
  background: varFigma(neutral-100-white) !important;
}

::v-deep .v-input--selection-controls__input {
  margin-right: 0 !important;
}

::v-deep .v-input--checkbox .v-label {
  left: 8px !important;
}

.card-container {
  justify-content: center;
}

.card-actions {
  display: flex;
  padding: var(--spacing-07, 24px);;
  position: absolute;
  bottom: 0;
  width: 100%;
  border-top: 1px solid var(--neutral-95-lightest, #EDEEF1);
  background: var(--neutral-100-white, #FFF);
}

.title {
  font-size: 18px !important;
  line-height: 120%;
  text-align: start;
}

.alert-container {
  width: 100%;
  display: flex;
  padding: var(--spacing-03, 8px) var(--spacing-04, 12px);
  align-items: center;
  gap: var(--spacing-04, 12px);
  align-self: stretch;
  border-radius: var(--spacing-02, 4px);
  border: 1px solid var(--feedback-warning, #FFC107);
  background: var(--feedback-warning-background, #FFF8F0);
}

.resource-container {
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  border-radius: 8px;
}

.bg-dark {
  .card-actions {
    border-top: 1px solid var(--neutral-95-lightest, #363949);
    background: var(--neutral-100-white, #171924);
  }

  .alert-container {
    background: var(--feedback-warning-background, #3D2300);
  }
}

@media (width < 768px) {
  p {
    white-space: normal;
  }

  ::v-deep .v-dialog {
    overflow: hidden !important;
  }

  .card-container {
    justify-content: flex-start !important;
  }

  .card-actions {
    position: fixed;
  }

  .step-one {
    margin-top: 130px;
  }
}
</style>
